import React from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, ChevronRight, Loader2, Sparkles, TrendingDown, TrendingUp } from 'lucide-react';
import { AlertModal, AppButton, cn } from '../../../ui';
import Shimmer from '../../../Common/Shimmer';
import DashCard from '../shared/DashCard';
import IconChip from '../shared/IconChip';
import ScoreRing from '../shared/ScoreRing';
import TileLink from '../shared/TileLink';
import useAiReport from '../useAiReport';
import { reportStanding } from '../studentHomeRules';

const STANDING_INK = {
  emerald: 'tw-text-success-fg',
  amber: 'tw-text-warning-fg',
  rose: 'tw-text-danger-fg',
};

const GRADE_CHIP = {
  emerald: 'tw-bg-success-soft',
  amber: 'tw-bg-warning-soft',
  rose: 'tw-bg-danger-soft',
};

const shortDate = (date) => new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

const LatestReport = ({ report }) => {
  const score = report.overallScore ?? 0;
  const standing = reportStanding(score);
  const Trend = standing.rising ? TrendingUp : TrendingDown;
  const updated = report.generatedAt || report.createdAt;
  const ink = STANDING_INK[standing.tone];

  return (
    <TileLink
      to={`/s/ai-report/${report._id}`}
      className="tw-block tw-rounded-[14px] tw-border tw-border-solid tw-border-primary/15 tw-bg-gradient-to-br tw-from-primary-soft tw-to-surface-raised tw-p-3.5 hover:tw-border-primary/30"
    >
      <div className="tw-flex tw-items-center tw-gap-3.5">
        <ScoreRing score={score} tone={standing.tone} label={`Overall score ${score}%`} className="tw-h-16 tw-w-16">
          <span className={cn('tw-text-sm tw-font-extrabold tw-leading-none', ink)}>{score}%</span>
        </ScoreRing>
        <div className="tw-min-w-0 tw-flex-1">
          <span className="tw-mb-1.5 tw-block tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">Overall Score</span>
          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            <span className={cn('tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[0.6875rem] tw-font-medium', GRADE_CHIP[standing.tone], ink)}>
              Grade {report.grade}
            </span>
            <span className={cn('tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-2 tw-py-0.5 tw-text-[0.6875rem] tw-font-medium', ink)}>
              <Trend size={11} aria-hidden="true" />
              {standing.label}
            </span>
          </div>
        </div>
      </div>
      <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-primary/10 tw-pt-2.5">
        <span className="tw-text-[0.6875rem] tw-font-medium tw-text-content-subtle">
          {updated ? `Updated ${shortDate(updated)}` : 'Latest report'}
        </span>
        <span className="tw-inline-flex tw-items-center tw-text-xs tw-font-semibold tw-text-primary">
          View report
          <ChevronRight size={14} aria-hidden="true" />
        </span>
      </div>
    </TileLink>
  );
};

const NoReport = () => (
  <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-[14px] tw-border tw-border-dashed tw-border-primary/25 tw-bg-primary-soft/50 tw-px-3.5 tw-py-4 tw-text-center">
    <IconChip icon={Sparkles} size={44} iconSize={22} className="tw-bg-surface-raised" />
    <p className="tw-m-0 tw-text-[0.8125rem] tw-font-medium tw-text-content">No AI report yet</p>
    <p className="tw-m-0 tw-text-xs tw-leading-snug tw-text-content-muted">
      Get a smart summary from your AI practice, mock interviews, and AIvo activity.
    </p>
  </div>
);

const AiReportCard = () => {
  const { report, loading, generating, error, generate, dismissError } = useAiReport();

  return (
    <DashCard title="AI Performance" icon={<IconChip icon={Sparkles} size={30} />}>
      <AlertModal show={!!error} onClose={dismissError} title="Could not generate report" message={error} variant="danger" />
      {loading ? <Shimmer height={132} /> : (
        <>
          {report ? <LatestReport report={report} /> : <NoReport />}
          <div className="tw-mt-3 tw-flex tw-gap-2">
            <AppButton className="tw-min-w-0 tw-flex-1" onClick={generate} disabled={generating}>
              {generating ? <Loader2 size={16} className="tw-animate-spin" /> : <Sparkles size={16} />}
              {generating ? 'Generating…' : 'Generate Report'}
            </AppButton>
            {report && (
              <AppButton as={Link} to="/s/ai-report/history" variant="outline" size="icon" aria-label="View report history">
                <BarChart3 size={16} />
              </AppButton>
            )}
          </div>
        </>
      )}
    </DashCard>
  );
};

export default AiReportCard;
